import { useState } from 'react';
import { X, Loader as Loader2, ShieldCheck } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { validatePassword } from '@/lib/passwordValidation';

interface Props {
  visible: boolean;
  onClose: () => void;
}

export default function ChangePasswordModal({ visible, onClose }: Props) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [needsMfa, setNeedsMfa] = useState(false);
  const [mfaFactorId, setMfaFactorId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);

  function reset() {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setTotpCode('');
    setNeedsMfa(false);
    setMfaFactorId('');
    setError('');
    setSuccess('');
    setPasswordErrors([]);
  }

  function handleClose() {
    reset();
    onClose();
  }

  async function handleSubmit() {
    setError('');
    setSuccess('');
    setPasswordErrors([]);

    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }

    const check = validatePassword(newPassword);
    if (!check.valid) {
      setPasswordErrors(check.errors);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('New password and confirmation do not match');
      return;
    }

    setLoading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user?.email) throw new Error('No user session');

      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: currentPassword,
      });
      if (signInError) {
        setError('Current password is incorrect');
        setLoading(false);
        return;
      }

      const { data: aalData } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();

      if (aalData && aalData.nextLevel === 'aal2' && aalData.currentLevel === 'aal1') {
        const { data: factorsData } = await supabase.auth.mfa.listFactors();
        const verifiedFactor = factorsData?.totp?.find((f) => f.status === 'verified');

        if (verifiedFactor) {
          setMfaFactorId(verifiedFactor.id);
          setNeedsMfa(true);
          setLoading(false);
          return;
        }
      }

      await updatePassword();
    } catch (err: any) {
      setError(err.message || 'Failed to change password');
      setLoading(false);
    }
  }

  async function handleMfaVerifyAndUpdate() {
    if (totpCode.length < 6) return;

    setError('');
    setLoading(true);

    try {
      const { data: challengeData, error: challengeError } =
        await supabase.auth.mfa.challenge({ factorId: mfaFactorId });

      if (challengeError) {
        setError(challengeError.message);
        setLoading(false);
        return;
      }

      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId: mfaFactorId,
        challengeId: challengeData.id,
        code: totpCode,
      });

      if (verifyError) {
        setError('Invalid 2FA code. Please try again.');
        setTotpCode('');
        setLoading(false);
        return;
      }

      await updatePassword();
    } catch (err: any) {
      setError(err.message || 'Verification failed');
      setLoading(false);
    }
  }

  async function updatePassword() {
    const { error: updateError } = await supabase.auth.updateUser({
      password: newPassword,
    });

    if (updateError) {
      setError(updateError.message);
    } else {
      setSuccess('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTotpCode('');
      setNeedsMfa(false);
      setMfaFactorId('');
    }
    setLoading(false);
  }

  if (!visible) return null;

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-md flex items-center justify-center p-4 z-50">
      <div className="glass-modal rounded-2xl p-6 w-full max-w-sm">
        <div className="flex justify-between items-center mb-5">
          <h2 className="text-base font-bold text-white">Change Password</h2>
          <button onClick={handleClose} className="p-1.5 glass-hover rounded-xl">
            <X size={18} className="text-white" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-400/30 rounded-xl">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}
        {success && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-xl">
            <p className="text-sm text-green-700">{success}</p>
          </div>
        )}
        {passwordErrors.length > 0 && (
          <div className="mb-4 p-3 bg-amber-500/10 border border-amber-400/30 rounded-xl">
            <p className="text-xs font-medium text-amber-700 mb-1">Password requirements:</p>
            <ul className="text-xs text-amber-600 space-y-0.5">
              {passwordErrors.map((e) => <li key={e}>- {e}</li>)}
            </ul>
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-white mb-1.5">Current Password</label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              disabled={needsMfa}
              className="w-full px-4 py-2.5 bg-white/10 border border-white/10 text-white rounded-xl text-sm focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none transition-all duration-200 disabled:bg-white/5 disabled:text-white/40"
              placeholder="Enter current password"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-white mb-1.5">New Password</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              disabled={needsMfa}
              className="w-full px-4 py-2.5 bg-white/10 border border-white/10 text-white rounded-xl text-sm focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none transition-all duration-200 disabled:bg-white/5 disabled:text-white/40"
              placeholder="Min 12 chars, upper/lower/number/symbol"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-white mb-1.5">Confirm New Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={needsMfa}
              className="w-full px-4 py-2.5 bg-white/10 border border-white/10 text-white rounded-xl text-sm focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none transition-all duration-200 disabled:bg-white/5 disabled:text-white/40"
              placeholder="Re-enter new password"
            />
          </div>

          {needsMfa && (
            <div>
              <div className="flex items-center gap-2 mb-1.5">
                <ShieldCheck size={13} className="text-teal-600" />
                <label className="block text-xs font-semibold text-white">2FA Verification Required</label>
              </div>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                autoComplete="one-time-code"
                autoFocus
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
                placeholder="000000"
                className="w-full px-4 py-2.5 bg-white/10 border border-teal-400/30 text-white rounded-xl text-sm text-center tracking-[0.3em] font-mono focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none transition-all duration-200"
              />
              <p className="text-[11px] text-white mt-1.5">Enter the 6-digit code from your authenticator app</p>
            </div>
          )}
        </div>

        <div className="flex gap-2.5 mt-5">
          <button
            onClick={handleClose}
            className="flex-1 px-4 py-2.5 text-sm glass-button text-white rounded-xl font-medium"
          >
            Cancel
          </button>
          {needsMfa ? (
            <button
              onClick={handleMfaVerifyAndUpdate}
              disabled={loading || totpCode.length < 6}
              className="flex-1 px-4 py-2.5 text-sm glass-button-active text-white rounded-xl font-medium disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : 'Verify & Update'}
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="flex-1 px-4 py-2.5 text-sm glass-button-active text-white rounded-xl font-medium disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : 'Change Password'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
